import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { LanguageService } from "../../shared/services";
import { environment } from "../../../environments/environment";
import { isPlatformBrowser, Location } from "@angular/common";

interface HomePageURI {
  url: string;
  queryParams: string;
}

/**
 * Single source of truth for each deployment's default (suffixless) language.
 * Keyed by `environment.platformId`. The suffix `/lang/<code>` is emitted only
 * when the active language differs from this default. NOTE: this registry holds
 * ONLY the default language — never domains (domains are environment-specific and
 * always come from `environment.*`).
 */
const PLATFORM_DEFAULT_LANG: { [platformId: string]: "pt" | "en" | "es" } = {
  br: "pt",
  mx: "es",
  co: "es",
  latam: "en"
};

/**
 * Centralized language -> URL suffix code map. The single place encode/decode of
 * the `/lang/<code>` segment is defined, so it can never drift from the parsing
 * in `getLanguageBasedOnURL`/`removeLanguageFromURL`.
 */
const LANG_SUFFIX_CODE: { [lang: string]: string } = {
  en: "en",
  pt: "pt-br",
  es: "es"
};

/**
 * Language -> URL slug PREFIX for the news-detail route. This is the ONLY route
 * whose slug word changes per language (`/news/...` in English, `/noticias/...`
 * in Portuguese & Spanish). Single source of truth shared by
 * `news.navigate.service` (link building) and the hreflang builder, so the two
 * can never advertise different URLs.
 */
const NEWS_SLUG_BY_LANG: { [lang: string]: string } = {
  en: "news",
  pt: "noticias",
  es: "noticias"
};

/**
 * Every leading slug that resolves to the news-detail component. Includes the
 * legacy accented `notícias`. The `insights/detail` and `insight-preview/detail`
 * shapes are matched separately in `isNewsDetailPath`.
 */
const NEWS_SLUG_ALIASES = ["news", "noticias", "notícias"];

@Injectable()
export class SiilaTranslationService {
  private currentLanguage: string;
  private supportedLanguages: string[] = ["pt", "en", "es"];
  private currentCode: string;

  constructor(
    private _ts: TranslateService,
    private languageService: LanguageService,
    private location: Location,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.currentCode = environment.platformId;
    if (this.currentCode == "mx" || this.currentCode == "co") {
      this.supportedLanguages = ["en", "es"];
    } else if (this.currentCode == "br") {
      this.supportedLanguages = ["pt", "en"];
    } else {
      this.supportedLanguages = ["en"];
    }
  }

  /**
   * Returns the default (suffixless) language for a deployment. Defaults to the
   * current deployment (`environment.platformId`). This is the single source of
   * truth shared by the URL builders and `app-initializer-factory.ts`.
   */
  getDefaultLanguage(
    platformId: string = environment.platformId
  ): "pt" | "en" | "es" {
    return PLATFORM_DEFAULT_LANG[platformId] || "en";
  }

  /**
   * The `/lang/<code>` URL segment for THIS deployment's default language
   * (`pt -> pt-br`, `es -> es`, `en -> en`). Single source of truth for callers
   * that need to strip ONLY the default-language suffix (e.g. canonical tags),
   * so the map never drifts from `applyLangSuffix`/`getLanguageBasedOnURL`.
   */
  getDefaultLangSuffixCode(
    platformId: string = environment.platformId
  ): string {
    return LANG_SUFFIX_CODE[this.getDefaultLanguage(platformId)];
  }

  /**
   * Public, read-only view of THIS deployment's supported languages. Returned as
   * a copy so callers (e.g. hreflang builders) cannot mutate the internal set.
   */
  getSupportedLanguages(): string[] {
    return [...this.supportedLanguages];
  }

  /**
   * Low-level suffix engine: appends `/lang/<code>` to `basePathOrUrl` ONLY when
   * `lang` differs from `defaultLang`. Normalizes `en-mx -> en` before deciding,
   * and strips a trailing slash so the suffix attaches cleanly. Pure (no
   * `window`/`history`) so it runs identically in browser and SSR.
   */
  applyLangSuffix(
    basePathOrUrl: string,
    lang: string,
    defaultLang: string
  ): string {
    let path = basePathOrUrl;
    if (path == null || path === undefined) {
      return path;
    }
    const normalizedLang = lang === "en-mx" ? "en" : lang;
    if (path.endsWith("/")) {
      path = path.slice(0, path.length - 1);
    }
    if (normalizedLang === defaultLang) {
      return path;
    }
    const code = LANG_SUFFIX_CODE[normalizedLang];
    return code ? path.concat("/lang/", code) : path;
  }

  getTranslation(textValue: string) {
    this.currentLanguage = this.getCurrentLanguage();
    return this.translateValue(textValue, this.currentLanguage);
  }

  /**
   * Resolve a multilingual JSON string to a SPECIFIC language (not necessarily
   * the active one), applying the same pt->pt-br->en / es->en / first-key
   * fallbacks as `getTranslation`. Kept pure so callers such as
   * `buildNewsDetailPath` can render a slug in any language regardless of the
   * language currently shown on screen.
   */
  private translateValue(textValue: string, language: string) {
    if (textValue) {
      try {
        let newValue = JSON.parse(textValue)[language];

        if (newValue == undefined && language === "pt") {
          newValue = JSON.parse(textValue)["pt-br"]
            ? JSON.parse(textValue)["pt-br"]
            : JSON.parse(textValue)["en"];
        }

        if (newValue == undefined && language === "es") {
          newValue = JSON.parse(textValue)["es"]
            ? JSON.parse(textValue)["es"]
            : JSON.parse(textValue)["en"];
        }

        if (typeof newValue === "object") {
          newValue = newValue[language];
          if (newValue == undefined && language === "pt") {
            newValue = newValue["pt-br"] ? newValue["pt-br"] : newValue["en"];
          }
          if (newValue == undefined && language === "es") {
            newValue = newValue["es"] ? newValue["es"] : newValue["en"];
          }
        }

        let result = newValue
          ? newValue
          : JSON.parse(textValue)[Object.keys(JSON.parse(textValue))[0]];

        return result;
      } catch (e) {
        console.error("error parsing --> ", textValue);
      }
    }
    return textValue;
  }

  /**
   * Normalize a URL path segment: lowercase, spaces/`%`/`?` -> `-`, and strip
   * quotes/brackets/ampersands/slashes. Mirrors the historical inline logic in
   * `news.navigate.service` so linked URLs and advertised hreflang URLs match
   * byte-for-byte.
   */
  slugify(text: string): string {
    return (text || "")
      .toLowerCase()
      .replaceAll(/[ %?]/g, "-")
      .replaceAll(/['"()<>&\\\/]/g, "");
  }

  /** The news-detail slug prefix for a language (`news` in EN, `noticias` in PT/ES). */
  getNewsSlugPrefix(lang: string): string {
    const normalized = lang === "en-mx" ? "en" : lang;
    return NEWS_SLUG_BY_LANG[normalized] || "news";
  }

  /**
   * Build the (suffixless) news-detail base path for a SPECIFIC language:
   * `<prefix(lang)>/<slugified headline-in-lang>/<id>`. Pure string op (no
   * `window`), so it is SSR-safe and can render any language's URL regardless of
   * the language currently active on screen.
   */
  buildNewsDetailPath(
    headlineJson: string,
    id: string | number,
    lang: string
  ): string {
    const normalized = lang === "en-mx" ? "en" : lang;
    const prefix = this.getNewsSlugPrefix(normalized);
    const titleSlug = this.slugify(
      this.translateValue(headlineJson, normalized)
    );
    return `${prefix}/${titleSlug}/${id}`;
  }

  /**
   * True when `path` targets the news-detail component (`DetailNewsComponent`):
   * any of the `news`/`noticias`/`notícias` slug prefixes, or the
   * `insights/detail` / `insight-preview/detail` shapes. Used to let the
   * component own hreflang for these routes instead of the generic builder.
   */
  isNewsDetailPath(path: string): boolean {
    if (!path) {
      return false;
    }
    const clean = path.split("?")[0].replace(/^\/+/, "");
    // `news/detail/:id` is a DIFFERENT component (NewsDetailComponent) that does
    // NOT manage its own hreflang, so leave it to the generic builder as before.
    if (clean.startsWith("news/detail/")) {
      return false;
    }
    const firstSegment = clean.split("/")[0];
    return (
      NEWS_SLUG_ALIASES.includes(firstSegment) ||
      clean.startsWith("insights/detail/") ||
      clean.startsWith("insight-preview/detail/")
    );
  }

  /**
   * URL-slug transform matching the `langSelector` pipe's URL mode: spaces ->
   * dashes, lowercased, with NO character stripping (keeps `&`, commas, accents).
   * This is intentionally DIFFERENT from `slugify` (news) — it must stay so, to
   * match the existing solution/product link convention byte-for-byte.
   */
  toUrlSlug(text: string): string {
    return (text || "")
      .split(" ")
      .join("-")
      .toLowerCase();
  }

  /**
   * Build the (suffixless) solution-detail base path for a SPECIFIC language:
   * `solutions/<id>/<langSelector-slug of the name-in-lang>`. The route ignores
   * the name segment (only `:id` drives content) but it must be language-correct
   * for hreflang. Pure string op -> SSR-safe.
   */
  buildSolutionDetailPath(
    nameJson: string,
    id: string | number,
    lang: string
  ): string {
    const normalized = lang === "en-mx" ? "en" : lang;
    return `solutions/${id}/${this.toUrlSlug(
      this.translateValue(nameJson, normalized)
    )}`;
  }

  /** Product twin of `buildSolutionDetailPath` (`products/<id>/<slug>`). */
  buildProductDetailPath(
    nameJson: string,
    id: string | number,
    lang: string
  ): string {
    const normalized = lang === "en-mx" ? "en" : lang;
    return `products/${id}/${this.toUrlSlug(
      this.translateValue(nameJson, normalized)
    )}`;
  }

  /** `solutions/:id/:name` detail route (numeric id) — owned by SolutionComponent. */
  isSolutionDetailPath(path: string): boolean {
    return this.isNumericDetailPath(path, "solutions");
  }

  /** `products/:id/:name` detail route (numeric id) — owned by GrocsComponent. */
  isProductDetailPath(path: string): boolean {
    return this.isNumericDetailPath(path, "products");
  }

  private isNumericDetailPath(path: string, prefix: string): boolean {
    if (!path) {
      return false;
    }
    const seg = path
      .split("?")[0]
      .replace(/^\/+/, "")
      .split("/");
    return seg[0] === prefix && /^\d+$/.test(seg[1] || "");
  }

  /**
   * True when the route's own component manages its hreflang tags (news, solution
   * or product detail). The generic builder in `app.component` must NOT emit
   * same-path tags for these, since only the component knows the per-language
   * translated slug.
   */
  componentOwnsHreflang(path: string): boolean {
    return (
      this.isNewsDetailPath(path) ||
      this.isSolutionDetailPath(path) ||
      this.isProductDetailPath(path)
    );
  }

  getImgSEOTranslation(translationKey: string): string {
    let key = "imgSEO." + translationKey;
    return this.getTranslationFromJSONFiles(key);
  }

  getTranslationFromJSONFiles(translationKey: string): string {
    let result = "";
    this._ts.get(translationKey).subscribe(data => {
      result = data;
    });
    return result;
  }

  getCurrentLanguage() {
    let language: string = "en";
    if (this.currentLanguage === undefined) {
      let storageValue = null;
      try {
        storageValue = localStorage.getItem("currentLanguageHP");
      } catch (error) {}

      if (storageValue) {
        language = storageValue;
      } else {
        language = this._ts.getBrowserLang();
      }
      return this.supportedLanguages.includes(language) ? language : "en";
    }

    return this.currentLanguage;
  }

  setLanguage(language: string, skipLangUrlPath?: boolean) {
    this.registrateLanguage(language);
    sessionStorage.setItem("language", language);
    this.currentLanguage = language;
    if (!skipLangUrlPath)
      this.addCurrentLanguageToPath(window.location.pathname);
    try {
      localStorage.setItem("currentLanguageHP", language);
      document.documentElement.setAttribute("lang", language);
    } catch (err) {}
    return this._ts.use(language);
  }

  getLanguageBasedOnURL(url: string) {
    const urlParts = url.split("/");
    let lang: string = urlParts[urlParts.length - 1];
    const indexOfQueryParams = lang.indexOf("?");
    if (indexOfQueryParams != -1) {
      lang = lang.substr(0, indexOfQueryParams);
    }

    switch (lang.toLowerCase()) {
      case "pt-br":
        return "pt";
      case "en":
        return "en";
      case "es":
        return "es";
      default:
        return undefined;
        break;
    }
  }

  registrateLanguage(language: string) {
    switch (language) {
      case "pt":
        this.languageService.registerCulture("pt-BR");
        break;
      case "en":
        this.languageService.registerCulture("en-US");
        if (environment.platformId == "mx") {
          this.languageService.registerCulture("en-mx");
          this._ts.setDefaultLang("en-mx");
          this._ts.use("en-mx");
        }
        break;
      case "es":
        this.languageService.registerCulture("es-MX");
        break;
      default:
        this.languageService.registerCulture("pt-BR");
    }
  }

  removeLanguageFromURL(url: string) {
    let newUrl = url;
    const urlParams = url.split("/");
    if (urlParams.length >= 2) {
      let langParam: string = urlParams[urlParams.length - 1];
      const langPathURL: string = urlParams[urlParams.length - 2];
      if (this.isLanguageParam(langParam)) {
        newUrl = urlParams.slice(0, urlParams.length - 2).join("/");
      } else if (langPathURL === "lang") {
        newUrl = urlParams.slice(0, urlParams.length - 2).join("/");
      }
    }
    return newUrl;
  }

  isLanguageParam(requestParam: string) {
    return this.supportedLanguages.includes(requestParam);
  }

  private getUrlExtension(url) {
    return url
      .split(/[#?]/)[0]
      .split(".")
      .pop()
      .trim();
  }

  public addCurrentLanguageToPath(currentPath: string) {
    if (currentPath) {
      const ext = this.getUrlExtension(currentPath);
      if (ext !== currentPath) {
        return currentPath;
      }
    }

    const homeURI = this.separateURIAndQueryParams(currentPath);
    const url = homeURI.url;
    const queryParams = homeURI.queryParams;

    let newURL = this.concatLanguagePathURL(this.removeLanguageFromURL(url));
    if (queryParams) {
      newURL = newURL.concat(queryParams);
    }
    // Address-bar rewrite is browser-only and guarded inside
    // updateUrlWithoutNavigation (SSR-safe).
    this.updateUrlWithoutNavigation(newURL);
    return newURL;
  }

  public updateUrlWithoutNavigation(newUrl: string) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const state = history.state;
    history.replaceState(state, "", newUrl);
  }

  private separateURIAndQueryParams(currentPath: string): HomePageURI {
    const indexQueryParam = currentPath.indexOf("?");
    let url = currentPath;
    let queryParams = null;

    if (indexQueryParam != -1) {
      url = currentPath.substring(0, indexQueryParam);
      queryParams = currentPath.substring(indexQueryParam, currentPath.length);
    }

    return { url: url, queryParams: queryParams };
  }

  /**
   * Thin delegate kept for backward compatibility. Drives the address-bar
   * normalization on every `NavigationEnd` (via `addCurrentLanguageToPath`). It
   * now emits the suffix conditionally — clean URL for the deployment default,
   * `/lang/<code>` otherwise — by delegating to `applyLangSuffix`.
   */
  concatLanguagePathURL(currentPath: string) {
    if (currentPath == null || currentPath === undefined) {
      return currentPath;
    }
    return this.applyLangSuffix(
      currentPath,
      this.currentLanguage,
      this.getDefaultLanguage()
    );
  }

  /**
   * The single canonical builder for INTERNAL (current-deployment) localized
   * links. Strips any pre-existing `/lang/<code>` segment, then appends the
   * suffix only for a non-default language. `lang` defaults to the active
   * language. The suffix decision uses the CURRENT platform's default.
   */
  buildLocalizedPath(basePath: string, lang?: string): string {
    if (basePath == null || basePath === undefined) {
      return basePath;
    }
    const language = lang || this.getCurrentLanguage();
    const homeURI = this.separateURIAndQueryParams(basePath);
    const cleanPath = this.removeLanguageFromURL(homeURI.url);
    let localized = this.applyLangSuffix(
      cleanPath,
      language,
      this.getDefaultLanguage()
    );
    if (homeURI.queryParams) {
      localized = localized.concat(homeURI.queryParams);
    }
    return localized;
  }

  /**
   * Single source of truth mapping a `platformId` to its absolute base URL.
   * Every base URL is sourced from `environment.*` (env-aware: LOCAL/STG/PROD) —
   * NO domain is hardcoded here. `latam`/`co` now have dedicated env fields, so
   * the previously hand-rolled `https://siila.com` / `https://siila.com.co`
   * literals live nowhere in the code.
   */
  getPlatformBaseUrl(platformId: string = environment.platformId): string {
    switch (platformId) {
      case "br":
        return environment.applicationURLBr;
      case "mx":
        return environment.applicationURLMx;
      case "latam":
        return environment.applicationURLLatam;
      case "co":
        return environment.applicationURLCo;
      default:
        return environment.applicationURL;
    }
  }

  private joinBaseAndPath(base: string, path: string): string {
    if (!path) {
      return base;
    }
    const b = base.endsWith("/") ? base.slice(0, base.length - 1) : base;
    const p = path.startsWith("/") ? path : "/".concat(path);
    return b.concat(p);
  }

  /**
   * Build an absolute URL to ANOTHER SiiLA deployment (cross-country navigation:
   * press, resource, the `es` redirect, etc.). The base URL is resolved from
   * `environment.*` via `getPlatformBaseUrl(targetPlatform)`, and the `/lang`
   * suffix is decided against the **target** platform's default language. When
   * `lang` is omitted it defaults to the target's default — i.e. a clean URL.
   */
  buildCrossCountryUrl(
    targetPlatform: string,
    path: string = "",
    lang?: string
  ): string {
    const base = this.getPlatformBaseUrl(targetPlatform);
    const targetDefault = this.getDefaultLanguage(targetPlatform);
    const language = lang || targetDefault;
    const fullUrl = this.joinBaseAndPath(base, path);
    return this.applyLangSuffix(fullUrl, language, targetDefault);
  }

  /**
   * Build an absolute URL into the MX `fibra-analytics` deep links. Always targets
   * the MX deployment; `lang` defaults to the active language, and the suffix is
   * applied against MX's default (`es`) — clean for `es`, `/lang/en` for `en`.
   */
  buildFibraAnalyticsUrl(
    path: string = "/fibra-analytics",
    lang?: string
  ): string {
    return this.buildCrossCountryUrl(
      "mx",
      path,
      lang || this.getCurrentLanguage()
    );
  }

  /**
   * Single policy point for analytics (Matomo/GA) tracking URLs. The user-facing
   * URL policy now omits the default-language `/lang` suffix, but analytics is
   * kept on its OWN policy here so historical dashboards are not fragmented:
   * this currently returns the tracking URL unchanged. Change ONLY this method to
   * alter the analytics URL shape deliberately (e.g. strip `/lang` for clean
   * aggregation) — every tracking call site routes through here.
   */
  buildTrackingUrl(url: string): string {
    return url;
  }
}
